import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"

import type { CommunicationContactPresentation } from "../../../_logic/communication-plan-table-contract"
import type { RecipientTagPresentation } from "./recipientPresentation"

const clean = (value: unknown): string | null => {
  const text = `${value ?? ""}`.trim()

  return text || null
}

const normalizedCode = (value: unknown) => clean(value)?.toUpperCase() ?? ""

const contactDateTimeFormatter = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
})

export const communicationContactDateTimeLabel = (value: string | null | undefined): string => {
  if (!value) return "Zeitpunkt unbekannt"

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return "Zeitpunkt unbekannt"

  const parts = Object.fromEntries(
    contactDateTimeFormatter
      .formatToParts(parsed)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  )

  return `${parts.day}.${parts.month}. · ${parts.hour}:${parts.minute} Uhr`
}

export const applyCommunicationContactEffectiveAt = (
  presentation: CommunicationContactPresentation,
  effectiveAt: string | null | undefined,
): CommunicationContactPresentation => {
  const recipientName = presentation.recipientName ?? "Unbekannter Empfänger"
  const targetLabel = presentation.targetLabel ?? "Kein Kontaktziel"
  const contactKindLabel = presentation.contactKindLabel ?? "Nachricht"
  const label = `${communicationContactDateTimeLabel(effectiveAt)} · ${contactKindLabel}`
  const subLabel = `${recipientName} · ${targetLabel}`

  return {
    ...presentation,
    label,
    subLabel,
    ariaLabel: `${label}, ${subLabel}`,
    sortValue: `${effectiveAt ?? ""} ${recipientName} ${contactKindLabel}`.trim(),
  }
}

export const communicationChannelDisplayLabel = (channel: string | null | undefined): string => {
  const labels: Record<string, string> = {
    EMAIL: "E-Mail",
    MOBILE_MESSAGE: "Mobile Nachricht",
    SMS: "SMS",
    RCS: "RCS",
    WHATSAPP: "WhatsApp",
    PHONE: "Telefon",
    VOICE: "Telefon",
    VOICE_BOT: "Telefonassistent",
    LETTER: "Brief",
    WEB_BOT: "Webchat",
    CHAT: "Chat",
    PORTAL: "Terminportal",
  }

  return labels[normalizedCode(channel)] ?? "Kontaktweg"
}

const purposeLabels: Record<string, string> = {
  APPOINTMENT_ANNOUNCEMENT: "Terminankündigung",
  APPOINTMENT_CONFIRMATION: "Terminbestätigung",
  APPOINTMENT_RESCHEDULE: "Terminverschiebung",
  APPOINTMENT_CANCELLATION: "Terminabsage",
  APPOINTMENT_CANCEL_REPLAN_LATER: "Terminabsage",
  APPOINTMENT_REMINDER: "Terminerinnerung",
  LETTER_DELIVERY: "Terminbrief",
  FOLLOW_UP: "Follow-up",
  WRONG_RECIPIENT: "Falscher Empfänger",
  CONTACT_DATA_UPDATE: "Kontaktdatenänderung",
  CALLBACK_REQUEST: "Rückrufbitte",
  GENERAL_INQUIRY: "Allgemeine Anfrage",
  HANDOVER: "Übergabe",
  BOOKED: "Terminbestätigung",
  RESCHEDULED: "Terminverschiebung",
  CANCEL_REPLAN_LATER: "Terminabsage",
}

const compactTemplatePurpose = (value: string | null | undefined): string | null => {
  const text = clean(value)

  if (!text) return null

  const compact = text
    .replace(/^Vorlage:\s*/i, "")
    .replace(/^E-Mail\s+(?:zur|bei)\s+/i, "")
    .replace(/^Mobile\s+/i, "")
    .replace(/^Telefonvorlage\s+/i, "Telefon ")
    .trim()
  const normalized = compact.toLocaleLowerCase("de-DE")

  if (normalized.includes("terminankündigung") || normalized.includes("ankündigung")) {
    return "Terminankündigung"
  }

  if (normalized.includes("terminbestätigung")) return "Terminbestätigung"

  if (normalized.includes("terminverschiebung") || normalized.includes("verschiebung")) {
    return "Terminverschiebung"
  }

  if (normalized.includes("stornierung") || normalized.includes("terminabsage"))
    return "Terminabsage"

  if (normalized.includes("follow-up")) return "Follow-up"

  return compact || null
}

export const communicationPurposeDisplayLabel = ({
  purpose,
  templateLabel,
}: {
  purpose?: string | null
  templateLabel?: string | null
}): string => {
  const purposeCode = normalizedCode(purpose)
  const templatePurpose = compactTemplatePurpose(templateLabel)

  if (purposeCode === "LETTER_DELIVERY" && templatePurpose) return templatePurpose

  return purposeLabels[purposeCode] ?? templatePurpose ?? "Nachricht"
}

const compactPurposeLabel = (purposeLabel: string): string => {
  const labels: Record<string, string> = {
    Terminankündigung: "Ankündigung",
    Terminbestätigung: "Bestätigung",
    Terminverschiebung: "Verschiebung",
    Terminabsage: "Absage",
    Terminerinnerung: "Erinnerung",
    Terminbrief: "Ankündigung",
  }

  return labels[purposeLabel] ?? purposeLabel
}

export const buildCommunicationContactPresentation = ({
  channel,
  recipient,
  channelDefinition,
  purpose = null,
  templateLabel = null,
  subject = null,
  effectiveAt = null,
}: {
  channel?: string | null
  recipient: RecipientTagPresentation
  channelDefinition: StatusDefinition
  purpose?: string | null
  templateKey?: string | null
  templateLabel?: string | null
  templateVersion?: string | null
  subject?: string | null
  messageDetailsHtml?: string | null
  effectiveAt?: string | null
}): CommunicationContactPresentation => {
  const channelLabel = communicationChannelDisplayLabel(channel)
  const purposeLabel = communicationPurposeDisplayLabel({ purpose, templateLabel })
  const compactPurpose = compactPurposeLabel(purposeLabel)
  const recipientName = clean(recipient.nameLabel) ?? "Unbekannter Empfänger"
  const target = clean(recipient.targetLabel) ?? "Kein Kontaktziel"
  const contactKindLabel = `${compactPurpose} per ${channelLabel}`
  const tooltipHtml = recipient.tooltipHtml
  const searchText = [
    recipientName,
    contactKindLabel,
    channelLabel,
    target,
    purposeLabel,
    templateLabel,
    subject,
  ]
    .filter(Boolean)
    .join(" ")

  return applyCommunicationContactEffectiveAt(
    {
      label: contactKindLabel,
      subLabel: `${recipientName} · ${target}`,
      recipientName,
      targetLabel: target,
      contactKindLabel,
      color: !recipient.isUsable
        ? "rose"
        : target === "Kein Kontaktziel"
          ? "orange"
          : channelDefinition.color,
      icon: recipient.icon || channelDefinition.icon,
      tooltipHtml,
      ariaLabel: `${contactKindLabel}, ${recipientName}, ${target}`,
      searchText,
      sortValue: `${recipientName} ${compactPurpose} ${channelLabel}`,
    },
    effectiveAt,
  )
}
